<script setup>
import { ref, watch } from 'vue'
import { toast } from '@/utils/toast'
import { copyToClipboard } from '@/utils/common'
import {
  vehicleMonitorTestVinStateByExtId,
  vehicleMonitorSearchByExtId,
  vehicleMonitorUnregisterExtId,
  vehicleMonitorTransactionState,
  vehicleMonitorSendPayloadCallbackByExtId,
  vehicleMonitorSopAddByExtId,
  vehicleMonitorSopDeleteByExtId,
  triggerPurchaseByExtId,
  checkTransactionStateByExtId,
  articlesCapabilityByExtId,
  extIdValidate
} from '@/api/cfes/vehicleExtIdMonitoring'

const extId = ref('')
const extIdRules = [v => !!v || 'External Id is required', v => v && v.length == 32 || 'External Id must be 32 characters']
const valid = ref(false)
const loading = ref(false)
const visible = ref(false)
const tab = ref(0)
const tabs = ref([])
const testVinState = ref(null)
const extIdWarning = ref('')

const previousExtId = ref('')
const isODPRegistered = ref(false)
const unregisterVinInfo = ref('')
const vin_confirm = ref(false)


// Payload sent to Callback Interface
const callbackPayloadTab = ref(0)
const callback_tabs = ['fod_Report_State_Job', 'Fod_Fetch_Fec_Job', 'Fod_push_capable_product', 'state_Of_Transaction', 'revocation_Receipts']
const fod_Report_State_Job_show = ref(true)
const Fod_Fetch_Fec_Job_show = ref(false)
const Fod_push_capable_product_show = ref(false)
const state_Of_Transaction_show = ref(false)
const revocation_Receipts_show = ref(false)
const fodFetchFecJob_transactionId = ref('')
const state_Of_Transaction_licenceState = ref('')
const state_Of_Transaction_transactionId = ref('')
const state_Of_Transaction_activationDate = ref('')
const state_Of_Transaction_expirationDate = ref('')
const autoActivationDate = ref(true)
const stateOfTransactionValid = ref(true)
const revocation_Receipts_revocationReceipts = ref([''])
const sendPayloadCallbackInfo = ref('')
const transactionIdRules = [t => !t || !t.includes(' ') || 'Transaction ID should not include spaces']
const iso8601Rules = [v => !v || /^(\d{4}-\d\d-\d\d([tT][\d:.]*)?)([zZ]|([+-])(\d\d):?(\d\d))?$/.test(v) || 'Date must be iso8601 format']

// Retrigger the Purchase Interface
const transactionId = ref('')
const purchase_disabled = ref(false)

const unregisterVinDialog = () => (vin_confirm.value = true)
const unregisterVinCancel = () => (vin_confirm.value = false)
const unregisterVin = () => {
  vin_confirm.value = false
  vehicleMonitorUnregisterExtId(extId.value).then(() => {
    toast.success('SUCCESS!')
  })
}

const vinSopHeaders = [
  { title: 'Sop Id', key: 'sopId' },
  { title: 'Created Time (UTC)', key: 'createdTime' },
  { title: 'Updated Time (UTC)', key: 'updatedTime' },
]
const vinSopSet = ref([])
const vinSopIdSelected = ref([])

// SOP management
const SOP_of_VIN_show = ref(true)
const sopDialog = ref(false)
const sop_id = ref('')
const dialog_confirm = ref(false)
const sopIdRules = [
  value => !!value || 'SOP ID is Required.',
  value => (value && value.length <= 32) || 'Max 32 characters',
]

// SOP add/delete methods
const openSopDialog = () => {
  sopDialog.value = true
}

const cancelSop = () => {
  sopDialog.value = false
  sop_id.value = ''
}

const saveSop = () => {
  if (!sop_id.value || sop_id.value.length > 32) {
    toast.warning('SOP ID is required and max 32 characters')
    return
  }
  const saveData = {
    extId: extId.value,
    sop_id: sop_id.value
  }
  vehicleMonitorSopAddByExtId(saveData).then(() => {
    sopDialog.value = false
    sop_id.value = ''
    toast.success('SUCCESS!')
    checkTestState()
  }).catch(() => {
    toast.error('Failed to add SOP')
  })
}

const delSop = () => {
  if (vinSopIdSelected.value.length === 0) {
    toast.warning('Please select at least one!')
    return
  }
  dialog_confirm.value = true
}

const dialog_confirm_del = () => {
  dialog_confirm.value = false
  if(vinSopIdSelected.value.length > 0) {
    const ids = vinSopIdSelected.value.join(',')
    const saveData = {
      extId: extId.value,
      sop_ids: ids
    }
    vinSopIdSelected.value = []
    vehicleMonitorSopDeleteByExtId(saveData).then(() => {
      toast.success('SUCCESS!')
      checkTestState()
    }).catch(() => {
      toast.error('Failed to delete SOP')
    })
  }else{
    toast.warning('Please select at least one!')
  }
}

const dialog_confirm_cancel = () => {
  dialog_confirm.value = false
}

const capableProducts_show = ref(true)
const capableProductsFunctionStateSet = ref([])
const vehicleFunctionStateHeaders = [
  { title: 'Function Id', key: 'functionId' },
  { title: 'Product Id', key: 'productId' },
  { title: 'Function State', key: 'functionState' },
  { title: 'Created Time (UTC)', key: 'createdTime' },
  { title: 'Updated Time (UTC)', key: 'updatedTime' },
]

const callbackUrlHeaders = [
  { title: 'Callback Url', key: 'callbackUrl' },
  { title: 'Callback For', key: 'callbackFor' },
  { title: 'Client', key: 'client' },
  { title: 'Created Time (UTC)', key: 'createdTime' },
  { title: 'Updated Time (UTC)', key: 'updatedTime' },
]
const callbackUrlSet = ref([])

const activationStateHeaders = [
  { title: 'Article Id', key: 'articleId' },
  { title: 'Transaction Id', key: 'transactionId' },
  { title: 'State', key: 'transactionState' },
  { title: 'Created Time (UTC)', key: 'createdTime' },
  { title: 'Updated Time (UTC)', key: 'updatedTime' },
]
const activationStateSet = ref([])
const activationStateExpanded = ref([])

// Revocation Transaction State
const revocationStateHeaders = [
  { title: 'Article Id', key: 'articleId' },
  { title: 'Transaction Id', key: 'transactionId' },
  { title: 'State', key: 'transactionState' },
  { title: 'Activation Transaction Id', key: 'purchaseTransactionId' },
  { title: 'Created Time (UTC)', key: 'createdTime' },
  { title: 'Updated Time (UTC)', key: 'updatedTime' },
]
const revocationStateSet = ref([])
const revocationStateExpanded = ref([])

const transactionStateHeaders = [
  { title: 'Licence State', key: 'licenceState' },
  { title: 'Lima Sync Status', key: 'limaSyncStatus' },
  { title: 'Callback URL', key: 'callbackUrl' },
  { title: 'Time', key: 'createdTime' },
]

const fetchTransactionStates = async (itemData, targetSet = activationStateSet) => {
  if (itemData.transactionStates !== undefined) return

  try {
    console.log('Fetching transaction states for:', itemData.transactionId)
    const response = await vehicleMonitorTransactionState(itemData.transactionId)
    console.log('Transaction states response:', response)
    // Find the item in the array and update it reactively
    const index = targetSet.value.findIndex(i => i.id === itemData.id)
    if (index !== -1) {
      targetSet.value[index] = {
        ...targetSet.value[index],
        transactionStates: response.articleTransactionStateList || []
      }
    }
  } catch (error) {
    console.error('Failed to fetch transaction states:', error)
    const index = targetSet.value.findIndex(i => i.id === itemData.id)
    if (index !== -1) {
      targetSet.value[index] = {
        ...targetSet.value[index],
        transactionStates: []
      }
    }
  }
}

// Watch activationStateExpanded to load data when row is expanded
watch(activationStateExpanded, (newExpanded, oldExpanded) => {
  console.log('Expanded changed:', newExpanded, oldExpanded)
  newExpanded.forEach(itemId => {
    const item = activationStateSet.value.find(i => i.id === itemId)
    if (item && item.transactionStates === undefined) {
      fetchTransactionStates(item, activationStateSet)
    }
  })
})

// Watch revocationStateExpanded to load data when row is expanded
watch(revocationStateExpanded, (newExpanded) => {
  newExpanded.forEach(itemId => {
    const item = revocationStateSet.value.find(i => i.id === itemId)
    if (item && item.transactionStates === undefined) {
      fetchTransactionStates(item, revocationStateSet)
    }
  })
})

// Helper function to get transaction states from the reactive array
const getItemTransactionStates = (itemId) => {
  const item = activationStateSet.value.find(i => i.id === itemId)
  return item?.transactionStates
}

const getRevocationItemTransactionStates = (itemId) => {
  const item = revocationStateSet.value.find(i => i.id === itemId)
  return item?.transactionStates
}

const vehicleStateHashHeaders = [
  { title: 'State Hash', key: 'stateHash' },
  { title: 'State', key: 'state' },
  { title: 'Created Time (UTC)', key: 'createdTime' },
  { title: 'Updated Time (UTC)', key: 'updatedTime' },
]
const vehicleStateHash = ref([])

const vehicleFsStateHeaders = [
  { title: 'Fs Id', key: 'fsId' },
  { title: 'Fs State', key: 'fsState' },
  { title: 'Created Time (UTC)', key: 'createdTime' },
  { title: 'Updated Time (UTC)', key: 'updatedTime' },
]
const vehicleFsStateSet = ref([])

const vehicleFunctionStateHeaders2 = vehicleFunctionStateHeaders
const vehicleFunctionStateSet = ref([])

const fecMetaDataHeaders = [
  { title: 'Metadata', key: 'metadata' },
  { title: 'FEC Hash', key: 'fecMd5Hash' },
  { title: 'FEC State', key: 'transmissionState' },
  { title: 'Created Time (UTC)', key: 'createdTime' },
  { title: 'Updated Time (UTC)', key: 'updatedTime' },
]
const fecMetaData = ref([])

// Raw data for state reports
const rawStateReportSet = ref(null)
const rawStateReportData = ref('')
const capablePayload = ref(null)
const capablePayloadToLM = ref(null)
const capablePayloadTimestamp = ref(null)
const capablePayloadToLMTimestamp = ref(null)
const rawCapableProductsData = ref('')
const rawCapableProductsToLMData = ref('')

// Show flags
const showRawCapableProducts = ref(false)
const showRawCapableProductsToLM = ref(false)
const showCapableProducts = ref(true)
const showArticlesCapability = ref(false)
const showRawStateReport = ref(false)
const showFsIDStateReport = ref(true)
const showFunctionIDStateReport = ref(false)

// Articles capability data
const articlesCapabilityData = ref([])
const articlesCapabilityDataLoading = ref(false)
const articlesCapabilityBtnCtl = ref(false)
const articlesCapabilityDataExpanded = ref([])
const showArticlesCapabilityHeaders = [
  { title: 'ArticleId', key: 'articleId' },
  { title: 'BundleId', key: 'bundleId' },
  { title: 'Is purchased', key: 'isPurchased' },
  { title: 'Is Purchasable', key: 'isPurchasable' },
]

const conversionStateReportData = () => {
  rawStateReportData.value = JSON.stringify(rawStateReportSet.value, undefined, 2) || ''
}

const conversionRawCapableProductsData = () => {
  rawCapableProductsData.value = JSON.stringify(capablePayload.value, undefined, 2) || ''
}

const conversionRawCapableProductsToLMData = () => {
  rawCapableProductsToLMData.value = JSON.stringify(capablePayloadToLM.value, undefined, 2) || ''
}

const showRawCapableProductsBtn = () => {
  showCapableProducts.value = false
  showRawCapableProductsToLM.value = false
  showRawCapableProducts.value = true
  showArticlesCapability.value = false
  conversionRawCapableProductsData()
}

const showRawCapableProductsToLMBtn = () => {
  showCapableProducts.value = false
  showRawCapableProductsToLM.value = true
  showRawCapableProducts.value = false
  showArticlesCapability.value = false
  conversionRawCapableProductsToLMData()
}

const showCapableProductsBtn = () => {
  showCapableProducts.value = true
  showRawCapableProductsToLM.value = false
  showRawCapableProducts.value = false
  showArticlesCapability.value = false
}

const showArticlesCapabilityBtn = () => {
  showCapableProducts.value = false
  showRawCapableProductsToLM.value = false
  showRawCapableProducts.value = false
  showArticlesCapability.value = true

  articlesCapabilityBtnCtl.value = true
  articlesCapabilityData.value = []
  articlesCapabilityDataLoading.value = true

  articlesCapabilityByExtId(extId.value).then(response => {
    articlesCapabilityDataLoading.value = false
    articlesCapabilityBtnCtl.value = false
    articlesCapabilityData.value = response.data || []
  }).catch(() => {
    articlesCapabilityDataLoading.value = false
    articlesCapabilityBtnCtl.value = false
    toast.error('Error')
  })
}

const showRawStateReportBtn = () => {
  showRawStateReport.value = true
  showFsIDStateReport.value = false
  showFunctionIDStateReport.value = false
  conversionStateReportData()
}

const showFsIDStateReportBtn = () => {
  showRawStateReport.value = false
  showFsIDStateReport.value = true
  showFunctionIDStateReport.value = false
}

const showFunctionIDStateReportBtn = () => {
  showRawStateReport.value = false
  showFsIDStateReport.value = false
  showFunctionIDStateReport.value = true
}

// Search backend
const search = (testFlag) => {
  // Clear expanded state before new search
  activationStateExpanded.value = []
  revocationStateExpanded.value = []
  vehicleMonitorSearchByExtId(testFlag, extId.value).then(response => {
    previousExtId.value = extId.value
    isODPRegistered.value = response.isODPRegistered
    activationStateSet.value = response.activationStateSet || []
    revocationStateSet.value = response.revocationStateSet || []
    callbackUrlSet.value = response.callbackUrlSet || []
    vinSopSet.value = response.vinSopSet || []
    vehicleStateHash.value = response.vehicleStateHash ? [response.vehicleStateHash] : []
    vehicleFsStateSet.value = response.vehicleFsStateSet || []
    vehicleFunctionStateSet.value = response.vehicleFunctionStateSet || []
    capableProductsFunctionStateSet.value = response.capableProductsFunctionStateSet || []
    fecMetaData.value = response.fecMetaData ? [response.fecMetaData] : []
    visible.value = true
    rawStateReportSet.value = response.raw ? JSON.parse(response.raw) : null
    capablePayload.value = response.capablePayload ? JSON.parse(response.capablePayload) : null
    capablePayloadTimestamp.value = response.capablePayloadTimestamp || null
    capablePayloadToLM.value = response.capablePayloadToLM ? JSON.parse(response.capablePayloadToLM) : null
    capablePayloadToLMTimestamp.value = response.capablePayloadToLMTimestamp || null
  })
}

// Watch testVinState
watch(testVinState, (val) => {
  const items = []
  const testItem = { tab: 'Test Data', isTestVin: true }
  const realItem = { tab: 'Real Data', isTestVin: false }
  const histRealItem = { tab: 'Historical Real Data', isTestVin: false }

  switch (val) {
    case 0:
      isODPRegistered.value = false
      activationStateSet.value = []
      revocationStateSet.value = []
      callbackUrlSet.value = []
      vinSopSet.value = []
      vehicleFsStateSet.value = []
      vehicleFunctionStateSet.value = []
      vehicleStateHash.value = []
      fecMetaData.value = []
      capableProductsFunctionStateSet.value = []
      visible.value = true
      toast.info('Sorry, no information for the VIN')
      break
    case 1:
      items.push(testItem)
      search(true)
      break
    case 2:
      items.push(realItem)
      search(false)
      break
    case 3:
      items.push(testItem, histRealItem)
      search(true)
      break
    default:
      break
  }
  loading.value = false
  tabs.value = items
})

const checkTestState = () => {
  if (!extId.value || extId.value.length !== 32) return
  testVinState.value = null
  extIdWarning.value = ''
  loading.value = true

  extIdValidate(extId.value).then(response => {
    const status = response.status
    if (status === 300) {
      extIdWarning.value = response.message || 'External Id validation returned a warning'
    } else if (status !== 200) {
      toast.error(response.message || 'VIN validation failed')
      loading.value = false
      return
    }

    vehicleMonitorTestVinStateByExtId(extId.value).then(res => {
      previousExtId.value = extId.value
      testVinState.value = res
      tab.value = 0
      loading.value = false
    })
  }).catch(() => {
    loading.value = false
  })
}

const reset = () => {
  extId.value = ''
  previousExtId.value = ''
  visible.value = false
  testVinState.value = null
  extIdWarning.value = ''
  vinSopSet.value = []
  callbackUrlSet.value = []
  activationStateSet.value = []
  revocationStateSet.value = []
  vehicleStateHash.value = []
  vehicleFsStateSet.value = []
  vehicleFunctionStateSet.value = []
  fecMetaData.value = []
  capableProductsFunctionStateSet.value = []
}

const pasteExtIdFilter = (event) => {
  event.preventDefault()
  extId.value = event.clipboardData.getData('text').trim()
}

const onTabClick = (item) => {
  search(item.isTestVin)
}

// Callback tab switch
const switch_callback_tab = (item) => {
  callback_tabs.forEach(element => {
    if (element === 'fod_Report_State_Job') fod_Report_State_Job_show.value = false
    if (element === 'Fod_Fetch_Fec_Job') Fod_Fetch_Fec_Job_show.value = false
    if (element === 'Fod_push_capable_product') Fod_push_capable_product_show.value = false
    if (element === 'state_Of_Transaction') state_Of_Transaction_show.value = false
    if (element === 'revocation_Receipts') revocation_Receipts_show.value = false
  })

  switch (item) {
    case 'fod_Report_State_Job':
      fod_Report_State_Job_show.value = true
      break
    case 'Fod_Fetch_Fec_Job':
      Fod_Fetch_Fec_Job_show.value = true
      break
    case 'Fod_push_capable_product':
      Fod_push_capable_product_show.value = true
      break
    case 'state_Of_Transaction':
      state_Of_Transaction_show.value = true
      break
    case 'revocation_Receipts':
      revocation_Receipts_show.value = true
      break
  }
}

// Send fod Report State Job
const send_fod_Report_State_Job = () => {
  if (confirm('Are you sure you want to send job?')) {
    const payload = { type: 'job', job: 'fodReportStateJob' }
    vehicleMonitorSendPayloadCallbackByExtId(extId.value, payload).then(() => {
      sendPayloadCallbackInfo.value = 'Send payload to callback interface succeed!'
      setTimeout(() => sendPayloadCallbackInfo.value = '', 2000)
    })
  }
}

// Send Fod Fetch Fec Job
const send_Fod_Fetch_Fec_Job = () => {
  if (!fodFetchFecJob_transactionId.value) {
    toast.warning('transactionId must not be empty!')
    return
  }
  if (confirm('Are you sure you want to send job?')) {
    const payload = { type: 'job', job: 'fodFetchFecJob', transactionId: fodFetchFecJob_transactionId.value }
    vehicleMonitorSendPayloadCallbackByExtId(extId.value, payload).then(() => {
      sendPayloadCallbackInfo.value = 'Send payload to callback interface succeed!'
      setTimeout(() => sendPayloadCallbackInfo.value = '', 2000)
    })
  }
}

// Send Fod Push Capable Product
const send_Fod_Push_Capable_Product = () => {
  if (confirm('Are you sure you want to send push capable product?')) {
    const payload = { type: 'pushCapableProduct' }
    vehicleMonitorSendPayloadCallbackByExtId(extId.value, payload).then(() => {
      sendPayloadCallbackInfo.value = 'Send push capable product succeed!'
      setTimeout(() => sendPayloadCallbackInfo.value = '', 2000)
    })
  }
}

// Send state Of Transaction
const send_state_Of_Transaction = () => {
  if (!state_Of_Transaction_licenceState.value) {
    toast.warning('licenceState must not be empty!')
    return
  }
  if (!state_Of_Transaction_transactionId.value) {
    toast.warning('transactionId must not be empty!')
    return
  }
  if (!autoActivationDate.value) {
    const iso8601Regex = /^(\d{4}-\d\d-\d\d([tT][\d:.]*)?)([zZ]|([+-])(\d\d):?(\d\d))?$/
    if (!iso8601Regex.test(state_Of_Transaction_activationDate.value)) {
      toast.warning('activationDate must be iso8601 format!')
      return
    }
    if (!iso8601Regex.test(state_Of_Transaction_expirationDate.value)) {
      toast.warning('expirationDate must be iso8601 format!')
      return
    }
  }
  if (confirm('Are you sure you want to send job?')) {
    const payload = {
      type: 'stateOfTransaction',
      stateOfTransaction: {
        licenceState: state_Of_Transaction_licenceState.value,
        transactionId: state_Of_Transaction_transactionId.value,
        activationDate: autoActivationDate.value ? null : state_Of_Transaction_activationDate.value,
        expirationDate: autoActivationDate.value ? null : state_Of_Transaction_expirationDate.value
      }
    }
    vehicleMonitorSendPayloadCallbackByExtId(extId.value, payload).then(() => {
      sendPayloadCallbackInfo.value = 'Send payload to callback interface succeed!'
      setTimeout(() => sendPayloadCallbackInfo.value = '', 2000)
    })
  }
}

// Revocation Receipts methods
const revocationReceipts_add = () => {
  revocation_Receipts_revocationReceipts.value.push('')
}

const revocationReceipts_delete = (index) => {
  if (revocation_Receipts_revocationReceipts.value.length === 1) {
    toast.warning('revocationReceipts cannot be null!')
    return
  }
  revocation_Receipts_revocationReceipts.value.splice(index, 1)
}

const send_revocation_Receipts = () => {
  for (let i = 0; i < revocation_Receipts_revocationReceipts.value.length; i++) {
    if (!revocation_Receipts_revocationReceipts.value[i]) {
      toast.warning('revocationReceipts cannot be empty!')
      return
    }
  }
  if (confirm('Are you sure you want to send job?')) {
    const payload = {
      type: 'revocationReceipts',
      revocationReceipts: revocation_Receipts_revocationReceipts.value
    }
    vehicleMonitorSendPayloadCallbackByExtId(extId.value, payload).then(() => {
      sendPayloadCallbackInfo.value = 'Send payload to callback interface succeed!'
      setTimeout(() => sendPayloadCallbackInfo.value = '', 2000)
    })
  }
}

// Trigger purchase
const trigger_purchase = () => {
  if (!transactionId.value) {
    toast.warning('transactionId must not be empty!')
    return
  }
  if (confirm('Are you sure you want to send job?')) {
    purchase_disabled.value = true
    checkTransactionStateByExtId(extId.value, transactionId.value).then(res => {
      if (res.data) {
        do_trigger_purchase()
      } else {
        if (confirm('This purchase was successful, do you want to send the request again?')) {
          do_trigger_purchase()
        } else {
          purchase_disabled.value = false
        }
      }
    }).catch(() => {
      purchase_disabled.value = false
    })
  }
}

const do_trigger_purchase = () => {
  purchase_disabled.value = true
  triggerPurchaseByExtId(extId.value, transactionId.value).then(response => {
    purchase_disabled.value = false
    if (response.status === 200) {
      toast.success('success')
    } else {
      toast.error(response.message || 'fail')
    }
  }).catch(() => {
    purchase_disabled.value = false
  })
}
</script>

<template>
  <v-container fluid class="page-shell">
    <v-alert
      v-if="extIdWarning"
      type="warning"
      variant="tonal"
      border="start"
      prominent
      closable
      class="mb-4"
      @click:close="extIdWarning = ''"
    >
      {{ extIdWarning }}
    </v-alert>
    <v-row class="text-center mb-4">
      <v-col cols="12">
        <h1 class="title font-weight-bold">Vehicle Monitoring</h1>
        <div class="headline-border"></div>
      </v-col>
    </v-row>
    <v-card class="section-card mb-4">
      <v-card-title>ExternalId Search</v-card-title>
      <v-card-text>
        <v-form ref="form" v-model="valid" lazy-validation>
          <v-row>
            <v-col cols="12" md="6">
              <v-text-field
                v-model="extId"
                class="vin-text-field"
                :counter="32"
                :rules="extIdRules"
                :disabled="loading"
                label="External ID"
                @paste="pasteExtIdFilter"
                required
              />
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12">
              <v-btn :disabled="!valid || loading" class="mr-3" size="small" color="primary" @click="checkTestState">Search</v-btn>
              <v-btn :disabled="loading" variant="tonal" size="small" color="primary" class="mr-3" @click="reset">Clear</v-btn>
            </v-col>
          </v-row>
        </v-form>
      </v-card-text>
    </v-card>

    <template v-if="loading">
      <div class="loader-container">
        <v-progress-circular indeterminate :size="120" :width="8" color="primary">loading</v-progress-circular>
      </div>
    </template>

    <template v-if="visible">
      <v-tabs class="pt-2 mb-4" v-model="tab">
        <v-tab v-for="(item, index) in tabs" :key="index" @click="onTabClick(item)">{{ item.tab }}</v-tab>
      </v-tabs>

      <v-card class="section-card mb-3">
        <v-card-title class="d-flex align-center justify-space-between">
          <span>External ID {{ previousExtId }} registered to ODP</span>
          <v-chip :color="isODPRegistered ? 'success' : 'grey'" variant="flat" size="small">{{ isODPRegistered ? 'YES' : 'NO' }}</v-chip>
        </v-card-title>
        <v-card-text>
          <v-btn v-if="isODPRegistered" size="small" class="mr-2" color="error" variant="outlined" @click="unregisterVinDialog">de-register External ID</v-btn>
          <span class="text-secondary">{{ unregisterVinInfo }}</span>
        </v-card-text>
      </v-card>

      <v-dialog v-model="vin_confirm" persistent max-width="400">
        <v-card>
          <v-card-title class="text-h6 grey lighten-2">Confirm</v-card-title>
          <v-card-text>Are you sure to de-register the External ID?</v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="primary" text @click="unregisterVin">Yes</v-btn>
            <v-btn color="primary" text @click="unregisterVinCancel">Cancel</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <v-card class="section-card mb-3">
        <v-card-title>SOP of External ID: {{ previousExtId }}</v-card-title>
        <v-card-text>
          <div class="mb-2" v-if="testVinState === 2">
            <v-btn size="small" class="mr-2" color="primary" @click="openSopDialog">Add</v-btn>
            <v-btn size="small" color="error" @click="delSop">Delete</v-btn>
          </div>
          <v-data-table
            dense
            v-model="vinSopIdSelected"
            :items="vinSopSet"
            :headers="vinSopHeaders"
            item-value="sopId"
            show-select
            disable-sort
            disable-pagination
            hide-default-footer
            class="elevation-1 monitoring-table"
          />
        </v-card-text>
      </v-card>

      <!-- Add SOP Dialog -->
      <v-dialog v-model="sopDialog" persistent max-width="600">
        <v-card>
          <v-card-title>Add SOP</v-card-title>
          <v-card-text>
            <v-text-field
              v-model="sop_id"
              label="SOP ID*"
              :rules="sopIdRules"
              required
            />
            <small>*indicates required field</small>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="blue-darken-1" text @click="cancelSop">Cancel</v-btn>
            <v-btn color="blue-darken-1" text @click="saveSop">Save</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Delete SOP Confirm Dialog -->
      <v-dialog v-model="dialog_confirm" persistent max-width="400">
        <v-card>
          <v-card-title class="text-h6 grey lighten-2">Confirm</v-card-title>
          <v-card-text>Are you sure to delete?</v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="primary" text @click="dialog_confirm_del">Delete</v-btn>
            <v-btn color="primary" text @click="dialog_confirm_cancel">Cancel</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <v-card class="section-card mb-3">
        <v-card-title class="d-flex align-center justify-space-between">
          <div v-if="showRawCapableProducts">Raw capable product from ODP of External ID: {{ previousExtId }}</div>
          <div v-if="showRawCapableProductsToLM">Raw capable product to LM of External ID: {{ previousExtId }}</div>
          <div v-if="showCapableProducts">Show capable products of External ID: {{ previousExtId }}</div>
          <div v-if="showArticlesCapability">Show unpurchasable function of External ID: {{ previousExtId }}</div>
          <v-btn icon size="small" @click="capableProducts_show = !capableProducts_show">
            <v-icon>{{ capableProducts_show ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text v-show="capableProducts_show">
          <div class="mb-2">
            <v-btn size="small" class="mr-2" @click="showRawCapableProductsBtn">Raw capable product from ODP</v-btn>
            <v-btn size="small" class="mr-2" @click="showRawCapableProductsToLMBtn">Raw capable product to LM</v-btn>
            <v-btn size="small" class="mr-2" @click="showCapableProductsBtn">Show capable products</v-btn>
            <v-btn size="small" :disabled="articlesCapabilityBtnCtl" :loading="articlesCapabilityDataLoading" @click="showArticlesCapabilityBtn">SHOW UNPURCHASABLE FUNCTION</v-btn>
          </div>
          <div v-if="showRawCapableProducts" class="d-flex justify-space-between align-center mt-2 mb-1">
            <span v-show="capablePayloadTimestamp" class="text-uppercase font-weight-bold">Timestamp: {{ capablePayloadTimestamp }}</span>
            <v-btn size="small" variant="text" @click="copyToClipboard(rawCapableProductsData)">
              <v-icon size="small" class="mr-1">mdi-content-copy</v-icon>Copy
            </v-btn>
          </div>
          <v-textarea v-if="showRawCapableProducts" readonly auto-grow :rows="10" v-model="rawCapableProductsData" class="mb-2" />
          <div v-if="showRawCapableProductsToLM" class="d-flex justify-space-between align-center mt-2 mb-1">
            <span v-show="capablePayloadToLMTimestamp" class="text-uppercase font-weight-bold">Timestamp: {{ capablePayloadToLMTimestamp }}</span>
            <v-btn size="small" variant="text" @click="copyToClipboard(rawCapableProductsToLMData)">
              <v-icon size="small" class="mr-1">mdi-content-copy</v-icon>Copy
            </v-btn>
          </div>
          <v-textarea v-if="showRawCapableProductsToLM" readonly auto-grow :rows="10" v-model="rawCapableProductsToLMData" class="mb-2" />
          <v-data-table
            v-if="showCapableProducts"
            dense
            :items="capableProductsFunctionStateSet"
            :headers="vehicleFunctionStateHeaders"
            disable-sort
            disable-pagination
            hide-default-footer
            class="elevation-1 monitoring-table"
          />
          <v-data-table
            v-if="showArticlesCapability"
            dense
            :items="articlesCapabilityData"
            :headers="showArticlesCapabilityHeaders"
            :loading="articlesCapabilityDataLoading"
            item-key="bundleId"
            disable-sort
            disable-pagination
            hide-default-footer
            class="elevation-1 monitoring-table"
            show-expand
            v-model:expanded="articlesCapabilityDataExpanded"
          >
            <template #expanded-row="{ columns, item }">
              <tr>
                <td :colspan="columns.length" class="pa-2">
                  <v-data-table
                    dense
                    hide-default-header
                    hide-default-footer
                    :headers="[{ title: '', key: 'reason' }]"
                    :items="item.reasons || []"
                    class="elevation-1"
                  />
                </td>
              </tr>
            </template>
          </v-data-table>
        </v-card-text>
      </v-card>

      <v-card class="section-card mb-3">
        <v-card-title>Callback Url for External ID: {{ previousExtId }}</v-card-title>
        <v-data-table dense :items="callbackUrlSet" :headers="callbackUrlHeaders" disable-sort disable-pagination hide-default-footer class="elevation-1 monitoring-table" />
      </v-card>

      <v-card class="section-card mb-3">
        <v-card-title>Activation Transaction State for External ID: {{ previousExtId }}</v-card-title>
        <v-data-table
          dense
          :items="activationStateSet"
          :headers="activationStateHeaders"
          item-value="id"
          disable-sort
          disable-pagination
          hide-default-footer
          class="elevation-1 monitoring-table"
          show-expand
          v-model:expanded="activationStateExpanded"
        >
          <template #expanded-row="{ item, columns }">
            <tr>
              <td :colspan="columns.length" class="pa-3">
                <div v-if="getItemTransactionStates(item.id) === undefined" class="text-center py-2">
                  <v-progress-circular indeterminate size="24" class="mr-2"></v-progress-circular>
                  <span class="ml-2">Loading transaction states...</span>
                </div>
                <template v-else>
                  <v-data-table
                    v-if="getItemTransactionStates(item.id) && getItemTransactionStates(item.id).length > 0"
                    dense
                    :items="getItemTransactionStates(item.id)"
                    :headers="transactionStateHeaders"
                    class="elevation-1"
                    hide-default-footer
                    disable-sort
                  />
                  <div v-else class="text-center py-2 text-grey">No transaction states available</div>
                </template>
              </td>
            </tr>
          </template>
        </v-data-table>
      </v-card>

      <v-card class="section-card mb-3">
        <v-card-title>Revocation Transaction State for External ID: {{ previousExtId }}</v-card-title>
        <v-data-table
          dense
          :items="revocationStateSet"
          :headers="revocationStateHeaders"
          item-value="id"
          disable-sort
          disable-pagination
          hide-default-footer
          class="elevation-1 monitoring-table"
          show-expand
          v-model:expanded="revocationStateExpanded"
        >
          <template #expanded-row="{ item, columns }">
            <tr>
              <td :colspan="columns.length" class="pa-3">
                <div v-if="getRevocationItemTransactionStates(item.id) === undefined" class="text-center py-2">
                  <v-progress-circular indeterminate size="24" class="mr-2"></v-progress-circular>
                  <span class="ml-2">Loading transaction states...</span>
                </div>
                <template v-else>
                  <v-data-table
                    v-if="getRevocationItemTransactionStates(item.id) && getRevocationItemTransactionStates(item.id).length > 0"
                    dense
                    :items="getRevocationItemTransactionStates(item.id)"
                    :headers="transactionStateHeaders"
                    class="elevation-1"
                    hide-default-footer
                    disable-sort
                  />
                  <div v-else class="text-center py-2 text-grey">No transaction states available</div>
                </template>
              </td>
            </tr>
          </template>
        </v-data-table>
      </v-card>

      <v-card class="section-card mb-3">
        <v-card-title>Current FoD stateHash of External ID: {{ previousExtId }}</v-card-title>
        <v-data-table dense :items="vehicleStateHash" :headers="vehicleStateHashHeaders" disable-sort disable-pagination hide-default-footer class="elevation-1 monitoring-table" />
      </v-card>

      <v-card class="section-card mb-3">
        <v-card-title>State reports of External ID: {{ previousExtId }}</v-card-title>
        <v-card-text>
          <div class="mb-2">
            <v-btn size="small" class="mr-2" @click="showRawStateReportBtn">Show Raw state report</v-btn>
            <v-btn size="small" class="mr-2" @click="showFsIDStateReportBtn">Show FsId State Report</v-btn>
            <v-btn size="small" @click="showFunctionIDStateReportBtn">Show FunctionId State Report</v-btn>
          </div>
          <v-textarea v-if="showRawStateReport" readonly auto-grow :rows="10" v-model="rawStateReportData" class="mb-2" />
          <v-data-table v-if="showFsIDStateReport" dense :items="vehicleFsStateSet" :headers="vehicleFsStateHeaders" disable-sort disable-pagination hide-default-footer class="elevation-1 monitoring-table mb-3" />
          <v-data-table v-if="showFunctionIDStateReport" dense :items="vehicleFunctionStateSet" :headers="vehicleFunctionStateHeaders2" disable-sort disable-pagination hide-default-footer class="elevation-1 monitoring-table" />
        </v-card-text>
      </v-card>

      <v-card class="section-card mb-3">
        <v-card-title>Newest FEC of External ID: {{ previousExtId }}</v-card-title>
        <v-data-table dense :items="fecMetaData" :headers="fecMetaDataHeaders" disable-sort disable-pagination hide-default-footer class="elevation-1 monitoring-table" />
      </v-card>

      <!-- Payload sent to Callback Interface -->
      <v-card class="section-card mb-3">
        <v-card-title>Payload sent to Callback Interface</v-card-title>
        <v-card-text>
          <v-tabs v-model="callbackPayloadTab" class="mb-4">
            <v-tab v-for="(item, index) in callback_tabs" :key="index" @click="switch_callback_tab(item)">{{ item }}</v-tab>
          </v-tabs>

          <v-card outlined class="pa-4">
            <!-- fod_Report_State_Job -->
            <v-form v-show="fod_Report_State_Job_show">
              <v-row>
                <v-col cols="12" md="6">
                  <v-text-field model-value="job" readonly label="type" />
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field model-value="fodReportStateJob" readonly label="job" />
                </v-col>
              </v-row>
              <v-btn size="small" class="mt-2" color="primary" @click="send_fod_Report_State_Job">Send job</v-btn>
            </v-form>

            <!-- Fod_Fetch_Fec_Job -->
            <v-form v-show="Fod_Fetch_Fec_Job_show">
              <v-row>
                <v-col cols="12" md="4">
                  <v-text-field model-value="job" readonly label="type" />
                </v-col>
                <v-col cols="12" md="4">
                  <v-text-field model-value="fodFetchFecJob" readonly label="job" />
                </v-col>
                <v-col cols="12" md="4">
                  <v-text-field
                    v-model="fodFetchFecJob_transactionId"
                    label="transactionId"
                    placeholder="ACPTI_687f4ab4-035b-11eb-adc1-0242ac120002"
                    :rules="transactionIdRules"
                  />
                </v-col>
              </v-row>
              <v-btn size="small" class="mt-2" color="primary" @click="send_Fod_Fetch_Fec_Job">Send job</v-btn>
            </v-form>

            <!-- Fod_push_capable_product -->
            <v-form v-show="Fod_push_capable_product_show">
              <v-row>
                <v-col cols="12" md="6">
                  <v-text-field model-value="pushCapableProduct" readonly label="type" />
                </v-col>
              </v-row>
              <v-btn size="small" class="mt-2" color="primary" @click="send_Fod_Push_Capable_Product">Send push capable product</v-btn>
            </v-form>

            <!-- state_Of_Transaction -->
            <v-form v-show="state_Of_Transaction_show" v-model="stateOfTransactionValid">
              <v-row>
                <v-col cols="12" md="6">
                  <v-text-field model-value="stateOfTransaction" readonly label="type" />
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="state_Of_Transaction_licenceState"
                    label="licenceState"
                    placeholder="JobError"
                  />
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="12">
                  <v-text-field
                    v-model="state_Of_Transaction_transactionId"
                    label="transactionId"
                    placeholder="ACPTI_687f4ab4-035b-11eb-adc1-0242ac120002"
                    :rules="transactionIdRules"
                  />
                </v-col>
              </v-row>
              <v-checkbox v-model="autoActivationDate" label="Filled out automatically based on the transactionId." />
              <v-card outlined class="pa-4 mb-3" v-if="!autoActivationDate">
                <v-row>
                  <v-col cols="12" md="6">
                    <v-text-field
                      v-model="state_Of_Transaction_activationDate"
                      label="activationDate"
                      placeholder="2022-07-19T00:14:30Z"
                      :rules="iso8601Rules"
                    />
                  </v-col>
                  <v-col cols="12" md="6">
                    <v-text-field
                      v-model="state_Of_Transaction_expirationDate"
                      label="expirationDate"
                      placeholder="2022-07-19T00:14:30Z"
                      :rules="iso8601Rules"
                    />
                  </v-col>
                </v-row>
              </v-card>
              <v-btn size="small" class="mt-2" color="primary" @click="send_state_Of_Transaction">Send job</v-btn>
            </v-form>

            <!-- revocation_Receipts -->
            <v-form v-show="revocation_Receipts_show">
              <v-row>
                <v-col cols="12">
                  <v-text-field model-value="revocationReceipts" readonly label="type" />
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="12">
                  <span class="font-weight-bold">revocationReceipts:</span>
                  <v-btn size="small" class="ml-4" @click="revocationReceipts_add">add</v-btn>
                </v-col>
              </v-row>
              <v-row v-for="(item, index) in revocation_Receipts_revocationReceipts" :key="index">
                <v-col cols="10" md="11">
                  <v-text-field
                    v-model="revocation_Receipts_revocationReceipts[index]"
                    placeholder="revocationReceipts"
                  />
                </v-col>
                <v-col cols="2" md="1" class="d-flex align-center">
                  <v-btn size="small" color="error" @click="revocationReceipts_delete(index)">delete</v-btn>
                </v-col>
              </v-row>
              <v-btn size="small" class="mt-2" color="primary" @click="send_revocation_Receipts">Send job</v-btn>
            </v-form>
          </v-card>

          <div class="mt-2 text-success">{{ sendPayloadCallbackInfo }}</div>
        </v-card-text>
      </v-card>

      <!-- Retrigger the Purchase Interface -->
      <v-card class="section-card mb-3">
        <v-card-title>Retrigger the Purchase Interface</v-card-title>
        <v-card-text>
          <v-tabs class="mb-4">
            <v-tab>PURCHASE</v-tab>
          </v-tabs>
          <v-card outlined class="pa-4">
            <v-form>
              <v-text-field
                v-model="transactionId"
                label="transactionId"
                :rules="transactionIdRules"
              />
              <v-btn :disabled="purchase_disabled" size="small" class="mt-2" color="primary" @click="trigger_purchase">Send Job</v-btn>
            </v-form>
          </v-card>
        </v-card-text>
      </v-card>
    </template>

  </v-container>
</template>

<style scoped>
.page-shell {
  padding: 8px 18px 28px;
}

.title {
  font-size: 1.1rem;
}

.headline-border {
  width: 80px;
  height: 4px;
  background: linear-gradient(90deg, #4f46e5, #06b6d4);
  margin: 12px auto 0;
  border-radius: 999px;
}

.section-card {
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  background: #fff;
  box-shadow: 0 6px 16px rgba(15, 23, 42, 0.05);
  padding: 10px 12px 14px;
}

.section-card :deep(.v-card-title) {
  font-size: 0.95rem;
  font-weight: 600;
}

.loader-container {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 48px 0;
}

.monitoring-table :deep(.v-data-table__wrapper) {
  border-radius: 10px;
  border: 1px solid #e5e7eb;
  overflow: hidden;
}

.monitoring-table :deep(tbody tr:nth-child(odd)) {
  background-color: #f9fafb;
}

.monitoring-table :deep(tbody tr:hover) {
  background-color: #eef2ff;
}

.form-actions {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  align-items: center;
}
</style>
